import { Typography } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import PropTypes from 'prop-types';
import React from 'react';
import ExternalResourceLink from '../../core/ExternalResourceLink';
import { globalStyles as styles } from '../../core/style/gstheme';
import { MEDIA_TYPES } from '../../core/utils/constants';
import { displayDate, getProjectFromGraph, isRTL, sortResourcesByLangPropList } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import CommonIriDisplay from '../../application-core/CommonIriDisplay';
import ContentDisplay from '../../core/ContentDisplay';

const DEFAULT_COLS = 4;
const DISABLE_RELATION_DELETION = false;

class ResourceDisplay extends React.Component {

  computeLangValues = (prop, langData) => {
    const data = langData || this.props.data;
    const viewLang = this.props.viewLang;
    let values;

    if (!(prop in data)) return [];

    // For ontology properties langstrings will take the @value form
    // For mutiple values is in array form
    // This will also handle rare situation where datatype is changed to lang string form somthing else
    // albeit with no fancy formatting of certain types
    if (Array.isArray(data[prop])) {
      values = data[prop]
        .filter(v => v.language === viewLang || !v.language)
        .map(v => v["@value"] ?
          v["@value"]
          :
          this.computeLiteralValue(v)
        );
    }
    else {
      // For a single non-aliased property value is in object form
      if (data[prop]["@value"]) {
        if (data[prop]["language"] === viewLang) {
          values = [data[prop]["@value"]];
        }
        else values = [];
      }
      else {
        // For aliased props they will take language objects
        values = data[prop][viewLang] ?
          Array.isArray(data[prop][viewLang]) ?
            [...data[prop][viewLang]]
            :
            [data[prop][viewLang]]
          :
          [];
      }
    }
    values.sort((a, b) => {
      if (a.toLowerCase() < b.toLowerCase()) return -1;
      return 1;
    });
    return values;
  };

  computeLiteralValue = (value) => {
    if (value === false) return "FALSE";
    if (value === true) return "TRUE";
    return value.toString();
  };

  computeDatatypeValues = (prop) => {
    const data = this.props.data;
    let values = prop in data ?
      Array.isArray(data[prop]) ?
        [...data[prop].map(dp => dp["@value"] ?
          dp["@value"]
          :
          dp
        )]
        :
        data[prop]["@value"] ?
          [this.computeLiteralValue(data[prop]["@value"])]
          :
          [this.computeLiteralValue(data[prop])]
      :
      [];
    // We convert some results into a more user-friendly output
    if (values && values.length > 0) {
      if (Array.isArray(data[prop])) {
        values = values.map((v, index) => {
          if (data[prop][index].type === "http://www.w3.org/2001/XMLSchema#date") {
            return displayDate(v);
          }
          else if (data[prop][index].type === "http://www.w3.org/2001/XMLSchema#dateTime") {
            return displayDate(v, true, true);
          }
          return v;
        });
      }
      else {
        // Should only be one value
        if (data[prop].type === "http://www.w3.org/2001/XMLSchema#date") {
          values = values.map(v => displayDate(v));
        }
        else if (data[prop].type === "http://www.w3.org/2001/XMLSchema#dateTime") {
          values = values.map(v => displayDate(v, true, true));
        }
      }
    }
    values.sort();
    return values;
  };

  computeRelationValues = (prop, graph, sourceGraph) => {
    const data = this.props.data;
    let values = prop in data && data[prop] !== undefined ?
      Array.isArray(data[prop]) ?
        [...data[prop]].map(dp => dp.id || dp)
        :
        [data[prop].id || data[prop]]
      :
      [];
    const resourceInAlias = GS.getAlias(this.props.resourceIn);
    const graphList = values.map(v => {
      let actualGraph = this.props.graph;
      if (this.props.graphInfo[v]) {
        // We try to find graphs that are used as linked projects for components
        const applicableGraphs = Object.keys(this.props.componentLinkedProjectsMap)
          .filter(lpGraph => this.props.componentLinkedProjectsMap[lpGraph].some(lp => {
            return data[resourceInAlias] && data[resourceInAlias].includes(lp);
          }));
        // From the linked project we try to find on that what the resource has been found in
        const applicableResourceGraphs = applicableGraphs.filter(ag => this.props.graphInfo[v].some(gi => gi.linkedGraph.includes(ag)));
        if (applicableResourceGraphs && applicableResourceGraphs.length > 0) {
          actualGraph = applicableResourceGraphs[0];
        }
      }
      return actualGraph;
    });
    const sortedValues = sortResourcesByLangPropList(values, graphList, ["prefLabel", "literalForm", "label", "title"], this.props.viewLang, this.props.defaultLang);
    values = sortedValues.map(v => {
      const valueIndex = values.findIndex((ov) => ov === v);
      let actualGraph = graphList[valueIndex];
      let actualComponents = this.props.components;
      return <CommonIriDisplay
        id={v}
        key={v}
        source={!this.props.editMode || DISABLE_RELATION_DELETION ? null : data.id}
        property={prop}
        propertyGraph={graph}
        components={graph !== actualGraph ? undefined : actualComponents}
        loadComponents={graph !== actualGraph}
        projectIRI={getProjectFromGraph(actualGraph)}
        showProject={graph !== actualGraph}
        projectType={this.props.projectType}
        handleNotAvailable={true}
        graph={actualGraph}
        sourceGraph={sourceGraph}
        viewLang={this.props.viewLang}
        defaultLang={this.props.defaultLang} />;
    });
    return values;
  };

  computeOutput = (values, truncate = false, treatContentAs, useRTL = false, marginTop = false) => {
    const rtl = useRTL ? isRTL(this.props.viewLang) : false;
    const output = values.map((label, index) => {
      const handleAsSpecialInput = treatContentAs && treatContentAs !== "text/plain";
      const outputText = truncate || !handleAsSpecialInput ? label.toString().substring(0, 200) : label.toString();
      return handleAsSpecialInput ?
        <div
          data-test={"span-" + index}
          key={index}
          style={{
            paddingBottom: 4,
            ...(index > 0 && treatContentAs !== MEDIA_TYPES.IMAGES) && { paddingTop: 4 },
            ...(index < values.length - 1 && treatContentAs !== MEDIA_TYPES.IMAGES && treatContentAs !== MEDIA_TYPES.SVG) &&
            {
              borderBottom: 'solid 2px #d0d0d0'
            },
            display: treatContentAs === MEDIA_TYPES.IMAGES || treatContentAs === MEDIA_TYPES.SVG ?
              'inline-block'
              :
              'block',
            ...(treatContentAs === MEDIA_TYPES.IMAGES || treatContentAs === MEDIA_TYPES.SVG) &&
            {
              width: '100px'
            },
            ...(treatContentAs === MEDIA_TYPES.MARKDOWN || treatContentAs === MEDIA_TYPES.HTML) &&
            {
              maxHeight: '230px'
            },
            marginRight: 6,
            marginBottom: 6,
            padding: 6,
            borderRadius: 4,
            overflow: 'auto',
            verticalAlign: 'top',
            backgroundColor: 'white',
            border: 'solid 1px #d0d0d0'
          }}>
          <ContentDisplay
            overrideMediaType={this.props.overrideMediaType}
            text={outputText}
            maxHeight="100px"
            treatContentAs={treatContentAs} />
        </div>
        :
        <Typography
          data-test={"value-" + index}
          key={index}
          dir={rtl ? "rtl" : null}
          lang={this.props.viewLang}
          className={this.props.classes.tableCellText}
          style={{
            ...(index < values.length - 1) && { borderBottom: 'solid 2px #d0d0d0' },
            ...(marginTop) && { borderTop: 'solid 2px #d0d0d0' },
            paddingBottom: 4,
            ...(index > 0 || marginTop) && { paddingTop: 4 },
            ...(rtl) && { textAlign: 'right' }
          }}>
          {outputText
            .split("\n")
            .map((t, index) => <span data-test={"span-" + index} key={index} style={{ display: 'block' }}>
              {t}
            </span>
            )
          }
          {
            (truncate || !handleAsSpecialInput) && label.length >= 200 &&
            <span data-test="truncated" style={{ display: 'inline-block', float: 'right', color: '#CA4F0C' }}>[Truncated]</span>
          }
        </Typography>;
    });
    return output;
  };

  computeLangStringOutput = (prop) => {
    try {
      GS.handle("CLO");
      const values = this.computeLangValues(prop);
      return this.computeOutput(values, true, null, true);
    }
    catch (e) {
      return "Error computing values";
    }
  };

  computeJoinLangStringOutput = (joinProp, propAlias) => {
    try {
      GS.handle("JCLO");
      const data = this.props.data;
      let values = joinProp in data && data[joinProp] !== undefined ?
        Array.isArray(data[joinProp]) ?
          [...data[joinProp]].map(dp => dp.id)
          :
          [data[joinProp].id]
        :
        [];
      const sortedValues = sortResourcesByLangPropList(values, this.props.graph, [propAlias], this.props.viewLang, this.props.defaultLang);
      const output = sortedValues.map(sv => {
        const data = GS.data(sv, this.props.graph, false);
        if (data) {
          const values = this.computeLangValues(propAlias, data);
          return this.computeOutput(values, true, null, true, true);
        }
        else
          return null;
      });
      return output;
    }
    catch (e) {
      return "Error computing values";
    }
  };

  computeDatatypeOutput = (data) => {
    try {
      const prop = data.value;
      GS.handle("CDO");
      const values = this.computeDatatypeValues(prop);
      return this.computeOutput(values, false, data.treatContentAs);
    }
    catch (e) {
      return "Error computing values";
    }
  };

  computeRelationOutput = (prop, graph, sourceGraph) => {
    try {
      GS.handle("CRO");
      const values = this.computeRelationValues(prop, graph, sourceGraph);
      return values;
    }
    catch (e) {
      return "Error computing values";
    }
  };

  render() {

    const { data, classes, defaultLang, graph, project, components, display, handleSelectionChange, canSelect,
      viewOptions, editMode, selected, innerDisplay } = this.props;

    const columnDisplay = display ?
      [...display]
      :
      [true, true, true, true];

    const additionalColumns = columnDisplay.slice(DEFAULT_COLS).filter(co => co !== undefined);
    const additionalOptions = viewOptions.slice(DEFAULT_COLS).filter(co => !co.heading);
    const isDeleted = data.pendingDeletion === true;
    const InnerDisplay = innerDisplay;
    const componentItemsProp = "inScheme" in data ?
      "inScheme"
      :
      "inDataset";

    return (
      <TableRow
        data-test={"resource-" + data.id}
        classes={{
          root: classes.stripedRow
        }}
        style={{ ...(isDeleted) && { backgroundColor: '#DD2C00' } }}>
        {editMode &&
          <TableCell className={classes.tableCell}>
            <Checkbox
              inputProps={{ 'aria-label': "Select or deselect item" }}
              disabled={!selected && (!canSelect || isDeleted)}
              data-test="select-item"
              size="small"
              color="primary"
              className={classes.checked}
              checked={selected}
              onClick={(ev) => handleSelectionChange(this.props.id, ev.target.checked)} />
          </TableCell>
        }
        <InnerDisplay
          computeLangStringOutput={this.computeLangStringOutput}
          {...this.props} />
        {additionalColumns.map((col, colIndex) => {
          if (col === false) return null;
          return <TableCell
            key={colIndex}
            className={classes.tableCell}
            style={{
              minWidth: '100px',
              maxWidth: '300px',
              paddingTop: 12,
              ...(isDeleted) && { color: 'white' }
            }}>
            {additionalOptions[colIndex].format === "langString" ?
              this.computeLangStringOutput(additionalOptions[colIndex].alias || additionalOptions[colIndex].value)
              :
              additionalOptions[colIndex].format === "datatype" ?
                this.computeDatatypeOutput(additionalOptions[colIndex])
                :
                additionalOptions[colIndex].format === "relation" ?
                  this.computeRelationOutput(additionalOptions[colIndex].alias || additionalOptions[colIndex].value, additionalOptions[colIndex].graph, graph)
                  :
                  <Typography className={classes.tableCellText}>
                    <span>Cannot compute value {additionalOptions[colIndex].toString()}</span>
                  </Typography>
            }
            {additionalOptions[colIndex].joinAlias &&
              this.computeJoinLangStringOutput(additionalOptions[colIndex].value, additionalOptions[colIndex].joinAlias)
            }
          </TableCell>;
        })}
        <TableCell className={classes.tableCell}
          style={{
            minWidth: '100px',
            maxWidth: '300px',
            paddingTop: 12
          }}>
          {data[componentItemsProp] && data[componentItemsProp].sort().map(isIn =>
            <div key={isIn}>
              {components.some(component => component.id === isIn) ?
                <CommonIriDisplay
                  key={isIn}
                  id={isIn}
                  viewLang={defaultLang}
                  handleNotAvailable={true}
                  defaultLang={defaultLang}
                  projectIRI={project}
                  isReadOnly={true}
                  graph={graph}
                  deeplink={true}
                  sourceType={data.type}
                  source={data.id} />
                :
                <ExternalResourceLink
                  key={isIn}
                  id={isIn}
                  graph={graph}
                  source={data.id}
                  isReadOnly={true}
                  property={componentItemsProp} />
              }
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  }
}

ResourceDisplay.defaultProps = {
  hideInScheme: false,
  showInScheme: false,
  selected: false,
  overrideMediaType: false,
  ontologyProjectAssociation: []
};

ResourceDisplay.propTypes = {
  classes: PropTypes.object,
  innerDisplay: PropTypes.object,
  canSelect: PropTypes.bool,
  display: PropTypes.array,
  baseLink: PropTypes.string.isRequired,
  editMode: PropTypes.bool,
  selected: PropTypes.bool,
  viewOptions: PropTypes.array.isRequired,
  ontologyProjectAssociation: PropTypes.array,
  components: PropTypes.array.isRequired,
  handleSelectionChange: PropTypes.func,
  data: PropTypes.object.isRequired,
  graph: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired,
  overrideMediaType: PropTypes.bool,
  graphInfo: PropTypes.object,
  componentLinkedProjectsMap: PropTypes.object,
  resourceIn: PropTypes.string,
  projectType: PropTypes.string.isRequired,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
};

export default withStyles(styles)(withGS(ResourceDisplay, ["ALL"], null, {}, true));